package com.castly.shared.dto

import kotlinx.serialization.Serializable

@Serializable
data class AudiobookDto(
    val id: String,
    val isbn: String? = null,
    val title: String,
    val subtitle: String? = null,
    val titleEnglish: String? = null,
    val subtitleEnglish: String? = null,
    val authorId: Int? = null,
    val authorName: String? = null,
    val readerId: Int? = null,
    val readerName: String? = null,
    val publisher: String? = null,
    val duration: Int? = null,
    val language: String? = null,
    val originalLanguage: String? = null,
    val description: String? = null,
    val copyrightYear: Int? = null,
    val releaseDate: String? = null,
    val coverImageUrl: String? = null,
    val seriesName: String? = null,
    val seriesPosition: String? = null,
    val isAbridged: Boolean? = null,
    val audienceRating: String? = null,
    val rssSourceUrl: String? = null,
    val labelId: Int? = null,
    val labelName: String? = null,
    val status: String = "DRAFT",
    val tags: List<String>? = null,
    val episodeCount: Int = 0,
    val isIncomplete: Boolean = false,
    val createdAt: String? = null,
    val updatedAt: String? = null,
    val deletedAt: String? = null
)

@Serializable
data class CreateAudiobookRequest(
    val isbn: String? = null,
    val title: String,
    val subtitle: String? = null,
    val titleEnglish: String? = null,
    val subtitleEnglish: String? = null,
    val authorId: Int? = null,
    val readerId: Int? = null,
    val publisher: String? = null,
    val duration: Int? = null,
    val language: String? = null,
    val originalLanguage: String? = null,
    val description: String? = null,
    val copyrightYear: Int? = null,
    val releaseDate: String? = null,
    val tagIds: List<String>? = null,
    val labelId: Int? = null
)

@Serializable
data class UpdateAudiobookRequest(
    val isbn: String? = null,
    val title: String? = null,
    val subtitle: String? = null,
    val titleEnglish: String? = null,
    val subtitleEnglish: String? = null,
    val authorId: Int? = null,
    val readerId: Int? = null,
    val publisher: String? = null,
    val duration: Int? = null,
    val language: String? = null,
    val originalLanguage: String? = null,
    val description: String? = null,
    val copyrightYear: Int? = null,
    val releaseDate: String? = null,
    val seriesName: String? = null,
    val seriesPosition: String? = null,
    val isAbridged: Boolean? = null,
    val audienceRating: String? = null,
    val tagIds: List<String>? = null,
    val labelId: Int? = null
)

@Serializable
data class AudiobookListResponse(
    val audiobooks: List<AudiobookDto>,
    val total: Int,
    val page: Int,
    val pageSize: Int
)

@Serializable
data class AudiobookEpisodeDto(
    val id: String,
    val episodeNumber: Int? = null,
    val title: String,
    val audioUrl: String? = null,
    val imageUrl: String? = null,
    val duration: Int? = null,
    val fileSize: Long? = null,
    val status: String,
    val publishedAt: String? = null,
    val createdAt: String,
    val hasArabicTranslation: Boolean = false,
    val deletedAt: String? = null,
    val downloadStatus: String? = null,
    val downloadError: String? = null
)

@Serializable
data class AudiobookEpisodesResponse(
    val episodes: List<AudiobookEpisodeDto>,
    val total: Int
)

@Serializable
data class AudiobookDetailDto(
    val id: String,
    val isbn: String? = null,
    val title: String,
    val subtitle: String? = null,
    val titleEnglish: String? = null,
    val subtitleEnglish: String? = null,
    val authorId: Int? = null,
    val authorName: String? = null,
    val readerId: Int? = null,
    val readerName: String? = null,
    val publisher: String? = null,
    val duration: Int? = null,
    val language: String? = null,
    val originalLanguage: String? = null,
    val description: String? = null,
    val copyrightYear: Int? = null,
    val releaseDate: String? = null,
    val coverImageUrl: String? = null,
    val seriesName: String? = null,
    val seriesPosition: String? = null,
    val isAbridged: Boolean? = null,
    val audienceRating: String? = null,
    val rssSourceUrl: String? = null,
    val labelId: Int? = null,
    val labelName: String? = null,
    val status: String = "DRAFT",
    val tags: List<String>? = null,
    val episodeCount: Int,
    val isIncomplete: Boolean = false,
    val createdAt: String? = null,
    val updatedAt: String? = null
)

@Serializable
data class EpisodeUploadResult(
    val audioUrl: String,
    val fileName: String,
    val fileSizeBytes: Long,
    val durationSeconds: Int? = null
)

@Serializable
data class BulkCreateEpisodesRequest(
    val episodes: List<BulkEpisodeItem>
)

@Serializable
data class BulkEpisodeItem(
    val title: String,
    val audioUrl: String,
    /** Sort key within the batch. Optional — omitting it keeps the submitted array order. */
    val displayOrder: Int = 0,
    val fileSizeBytes: Long? = null,
    val durationSeconds: Int? = null
)

@Serializable
data class BulkCreateEpisodesResponse(
    val created: Int,
    val startingEpisodeNumber: Int,
    val episodes: List<BulkCreatedEpisodeDto>
)

@Serializable
data class BulkCreatedEpisodeDto(
    val id: String,
    val title: String,
    val episodeNumber: Int
)
